import classNames from 'classnames';
import { MapContainer, Marker, Polyline, TileLayer } from 'react-leaflet';
import { GeoRasterLayer } from './Common/GeoRasterLayer.tsx';
import {
  createContext,
  Fragment,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CategoryColor,
  Coloring,
  ColoringType,
  DataLayer,
  DataLayerData,
  DataLayerIcon,
  DataLayerParameter,
  DataLayerPlain,
  DataLayerStory,
  DataLayerType,
  getGraph,
  getProjects,
  Graph as GraphApiType,
  ParameterType,
  Project,
  SourceGeoJson,
  SourceGeoRaster,
  SourceTilesTMS,
  SourceType,
} from '../api/api.ts';
import { ModalContext } from '../common/Modal.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faCircleInfo,
  faClockRotateLeft,
  faEye,
  faEyeSlash,
  faLayerGroup,
  faLock,
  faLockOpen,
  faNetworkWired,
  faPlus,
  faScroll,
  faTimes,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import GeoJsonLayer from './Common/GeoJsonLayer.tsx';
import { Icon, LatLng, LatLngBounds } from 'leaflet';
import ColoredTileLayer from './Common/TileLayer.tsx';
import Loading from '../common/Loading.tsx';
import Disclaimer from './Disclaimer.tsx';
import GraphLink from '../common/GraphLink.tsx';
import NeumorphBox, { NeumorphType } from '../common/ui/NeumorphBox.tsx';
import Panel from '../common/Panel.tsx';
import NewGraph from './NewGraph.tsx';
import {
  gradientClassesForColor,
  hoverTextColorClassesForColor,
} from '../utils/Colors.ts';
import { ZoomControl } from './ZoomControl.tsx';
import { CoordinatePickerHandle } from './CoordinatePickerHandle.tsx';
import PanelLegend from './PanelLegend.tsx';
import PanelContent from './PanelContent.tsx';
import PanelStory from './PanelStory.tsx';
import PanelPlots, { LayerAndParams } from './PanelPlots.tsx';
import { cn } from '@/lib/utils.ts';
import { LanguageContext } from '@/common/Language.tsx';

const {
  // VITE_MAPBOX_USERNAME: MAPBOX_USERNAME,
  // VITE_MAPBOX_STYLE_ID: MAPBOX_STYLE_ID,
  // VITE_MAPBOX_ACCESS_TOKEN: MAPBOX_ACCESS_TOKEN,
  VITE_BACKEND_BASE_URL: BACKEND_BASE_URL,
} = import.meta.env;

const initialMapCenter: [number, number] = [52.353416, 13.805501];
const initialMapZoom: number = 9;
const storyMarkerDefaultZoom: number = 13;

export type ResetViewCallback = (
  mainProjectId: string,
  furtherPrimaryProjectIds: string[]
) => void;

export type DataLayerParameterValues = Record<
  string,
  string | number | [number, number] | [number | string, number | string]
>;

const MarkerIcon = new Icon({
  iconUrl: BACKEND_BASE_URL + '/static/marker-icon.png',
  iconSize: [48, 48],
  iconAnchor: [24, 45],
});
const MarkerIconOrange = new Icon({
  iconUrl: BACKEND_BASE_URL + '/static/marker-icon-orange.png',
  iconSize: [48, 48],
  iconAnchor: [24, 45],
});
const MarkerIconInactive = new Icon({
  iconUrl: BACKEND_BASE_URL + '/static/marker-icon-inactive.png',
  iconSize: [48, 48],
  iconAnchor: [24, 45],
});
const MarkerIconRed = new Icon({
  iconUrl: BACKEND_BASE_URL + '/static/marker-icon-red.png',
  iconSize: [48, 48],
  iconAnchor: [24, 45],
});
const MarkerIconGreen = new Icon({
  iconUrl: BACKEND_BASE_URL + '/static/marker-icon-green.png',
  iconSize: [48, 48],
  iconAnchor: [24, 45],
});
const MarkerIconBlue = new Icon({
  iconUrl: BACKEND_BASE_URL + '/static/marker-icon-blue.png',
  iconSize: [48, 48],
  iconAnchor: [24, 45],
});
const getColoredMarkerIcon = (color: CategoryColor) => {
  return {
    [CategoryColor.Blue]: MarkerIconBlue,
    [CategoryColor.Red]: MarkerIconRed,
    [CategoryColor.Green]: MarkerIconGreen,
  }[color];
};

export const substituteParameters = (
  url: string,
  parameterValues: DataLayerParameterValues,
  parameters: Record<string, DataLayerParameter>
): string => {
  let result = url;
  for (const key in parameterValues) {
    switch (parameters[key].type) {
      case ParameterType.Coordinate:
        const coordinateValue = parameterValues[key] as [number, number];
        result = result.replace(
          `{:${key}:}`,
          coordinateValue.map((v) => v.toString()).join('+++')
        );
        break;
      case ParameterType.DependentChoice:
        const depChoiceValue = parameterValues[key] as [
          string | number,
          string | number,
        ];
        result = result.replace(
          `{:${key}:}`,
          depChoiceValue.map((v) => v.toString()).join('+++')
        );
        break;
      default:
        result = result.replace(`{:${key}:}`, parameterValues[key].toString());
        break;
    }
  }
  return result;
};

export const getVisibilityKey = (projectId: string, dataLayerId?: string) => {
  return `${projectId}${dataLayerId !== undefined ? `+${dataLayerId}` : ''}`;
};

export const iconForDataLayer = (dataLayer: DataLayer) => {
  const mapping: { [key in DataLayerIcon]: IconDefinition } = {
    [DataLayerIcon.Story]: faScroll,
    [DataLayerIcon.Static]: faLayerGroup,
    [DataLayerIcon.Series]: faClockRotateLeft,
    [DataLayerIcon.Model]: faNetworkWired,
  };
  return mapping[dataLayer.icon];
};

// @todo check threshold loadProject bug

export type CoordinatePickerCallback = (latlng: LatLng) => void;
export const CoordinatePickerContext = createContext<{
  currentRequest: CoordinatePickerCallback | null;
  requestCoordinate: (callback: CoordinatePickerCallback) => void;
  resetRequest: () => void;
} | null>(null);

export const useCoordinatePickerContext = () => {
  const context = useContext(CoordinatePickerContext);
  if (!context) throw 'MapContext not initialized';
  return context;
};

export enum ViewType {
  Story = 'story',
  Regular = 'regular',
}
export enum ContentDisplayStyle {
  Hidden = 'hidden',
  Short = 'short',
  Full = 'full',
}

export type View =
  | null
  | {
      type: ViewType.Story;
      project: Project;
      layer: DataLayerStory;
      markerIndex: number;
    }
  | {
      type: ViewType.Regular;
      project: Project;
      contentStyle: ContentDisplayStyle;
    };

export const MapContext = createContext<{
  projects: Project[];
  graph: GraphApiType;
  subcategoryColors: Record<string, CategoryColor>;
  colorForSubcategoryId: (subcategoryId: string) => CategoryColor;
  gradientClassesForSubcategoryId: (subcategoryId: string) => string[];
  projectById: (projectId: string) => Project | undefined;
  activeProjects: Project[] | null;
  inactiveProjects: Project[] | null;
  projectAndLayerById: (
    layerId: string
  ) => { project: Project; layer: DataLayer } | undefined;
  view: View;
  viewReset: () => void;
  viewSetProject: (projectId: string) => boolean;
  shortContentRerender: boolean;
  shortContentTimeout: number;
  resetShortContentTimeout: () => void;
  storyLeave: () => void;
  storyNavigate: (increase: boolean) => boolean;
  storyNavigateJump: (
    project: Project,
    layer: DataLayerStory,
    markerIndex?: number
  ) => void;
  setContentDisplayStyle: (contentStyle: ContentDisplayStyle) => void;
  hideSidebar: boolean;
  resetView: (
    mainProjectId: string,
    furtherPrimaryProjectIds: string[]
  ) => void;
  showGraph: () => void;
  addProjectToPrimaries: (projectId: string) => void;
  removeProjectFromPrimaries: (projectId: string) => void;
  updateOrToggleVisibility: (
    projectId: string,
    layer: DataLayer | null,
    value?: boolean
  ) => void;
  isVisible: (projectId: string, layerId?: string) => boolean;
  dataLayersForLegend: DataLayerData[];
  getParameterValues: (layerId: string) => DataLayerParameterValues;
  updateParameterValues: (
    layerId: string,
    parameterValues: DataLayerParameterValues
  ) => void;
  coordinatePickerLocations: [number, number][];
} | null>(null);

export const useMapContext = () => {
  const context = useContext(MapContext);
  if (!context) throw 'MapContext not initialized';
  return context;
};

export default function Map({}: {}) {
  const { t, toggleLang } = useContext(LanguageContext);
  const { show: showModal, close: closeModal } = useContext(ModalContext);

  const [plotPanelLarge, setPlotPanelLarge] = useState<boolean | undefined>(
    undefined
  );

  const [zoomLock, setZoomLock] = useState<boolean>(false);

  // Filter types: 'story' for story layers, or DataLayerIcon for data layers
  const [iconFilters, setIconFilters] = useState<Set<DataLayerIcon>>(new Set());

  const [coordinatePickerCallback, setCoordinatePickerCallback] =
    useState<CoordinatePickerCallback | null>(null);

  const [flyToMaxZoom, setFlyToMaxZoom] = useState<number>(
    storyMarkerDefaultZoom
  );
  const [flyToBounds, setFlyToBounds] = useState<LatLngBounds | null>(null);
  const flyToMarkers = (markers: [number, number][]) => {
    if (zoomLock) return;
    const bounds = new LatLngBounds(markers);
    setFlyToMaxZoom(storyMarkerDefaultZoom);
    setFlyToBounds(bounds);
  };

  const flyToDataLayer = (layer: DataLayerData) => {
    if (zoomLock) return;
    const bounds = layer.dataSources.flatMap((dataSource) => {
      switch (dataSource.dataSource.type) {
        case SourceType.TilesTMS:
          return (dataSource.dataSource as SourceTilesTMS).bounds;
        case SourceType.GeoJson:
          return (dataSource.dataSource as SourceGeoJson).bounds;
        default:
          return [];
      }
    });
    if (bounds.length) {
      const boundsObject = new LatLngBounds(bounds);
      setFlyToMaxZoom(storyMarkerDefaultZoom);
      setFlyToBounds(boundsObject);
    }
  };

  // tmp
  const [disclaimerRead, setDisclaimerRead] = useState(false);
  const onDisclaimerRead = () => {
    localStorage.setItem('disclaimerRead', 'true');
    setDisclaimerRead(true);
  };
  useEffect(() => {
    // todo uncomment
    if (localStorage.getItem('disclaimerRead') === 'done')
      setDisclaimerRead(true);
  });
  // tmp

  const [projects, setProjects] = useState<Project[] | null>(null);
  const [graph, setGraph] = useState<GraphApiType | null>(null);
  const subcategoryColors = useMemo((): Record<string, CategoryColor> => {
    if (!graph) return {};
    const result: { [key: string]: CategoryColor } = {};
    graph.categories.forEach((cat) => {
      cat.subcategories.forEach((sub) => {
        result[sub.id] = cat.color;
      });
    });
    return result;
  }, [graph]);
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  const [activeProjectIds, setActiveProjectIds] = useState<string[]>([]);

  const colorForSubcategoryId = useCallback(
    (subcategoryId: string): CategoryColor => {
      const color = subcategoryColors[subcategoryId];
      if (!color)
        throw `Unknown CategoryColor for subcategory ID ${subcategoryId}`;
      return color;
    },
    [subcategoryColors]
  );
  const gradientClassesForSubcategoryId = (subcategoryId: string): string[] => {
    const color = subcategoryColors[subcategoryId];
    if (!color)
      throw `Unknown CategoryColor for subcategory ID ${subcategoryId}`;
    return gradientClassesForColor(color);
  };
  const gradientHoverClassesForSubcategoryId = (
    subcategoryId: string
  ): string[] => {
    const color = subcategoryColors[subcategoryId];
    if (!color)
      throw `Unknown CategoryColor for subcategory ID ${subcategoryId}`;
    switch (color) {
      case CategoryColor.Red:
        return [
          'hover:bg-linear-to-br',
          'hover:from-cliwac-red-light',
          'hover:to-cliwac-red',
        ];
      case CategoryColor.Green:
        return [
          'hover:bg-linear-to-br',
          'hover:from-cliwac-green-light',
          'hover:to-cliwac-green',
        ];
      case CategoryColor.Blue:
        return [
          'hover:bg-linear-to-br',
          'hover:from-cliwac-blue-light',
          'hover:to-cliwac-blue',
        ];
    }
  };

  const [addingPrimaryProject, setAddingPrimaryProject] = useState(false);
  // @todo add notification for loosing setting when using graph navigation

  const [view, setView] = useState<View>(null);

  const needsControls = useCallback((layer: DataLayerData): boolean => {
    return Boolean(Object.keys(layer.parameters).length);
  }, []);

  const viewReset = () => {
    setView(null);
  };

  const [bypassShortContent, setBypassShortContent] = useState(false);
  const shortContentTimeout = useRef<number>(0);
  const [shortContentRerender, setShortContentRerender] = useState(false);
  const rerenderContentTimeout = () => {
    setShortContentRerender((state) => !state);
  };

  const resetShortContentTimeout = useCallback(() => {
    if (shortContentTimeout.current) {
      clearTimeout(shortContentTimeout.current);
      shortContentTimeout.current = 0;
      rerenderContentTimeout();
    }
  }, []);

  const viewSetProject = (
    projectId: string,
    skipTimeout?: boolean,
    calledFromVisibilityToggle?: boolean
  ): boolean => {
    if (view !== null && view.project.id === projectId) return false;
    const project = projectById(projectId);
    if (!project) throw `Cannot find project with id ${projectId}`;

    if (
      !bypassShortContent &&
      !skipTimeout &&
      !(
        view &&
        view.type === ViewType.Regular &&
        view.project.id === projectId &&
        view.contentStyle === ContentDisplayStyle.Short
      )
    ) {
      shortContentTimeout.current = window.setTimeout(() => {
        setView((viewState) => {
          resetShortContentTimeout();
          if (
            viewState &&
            viewState.type === ViewType.Regular &&
            viewState.project.id === projectId &&
            viewState.contentStyle === ContentDisplayStyle.Short
          ) {
            return {
              ...viewState,
              contentStyle: ContentDisplayStyle.Hidden,
            };
          }
          return viewState;
        });
      }, 3000);
      rerenderContentTimeout();
    }
    setView({
      type: ViewType.Regular,
      project: project,
      contentStyle: ContentDisplayStyle.Short,
    });
    if (!calledFromVisibilityToggle)
      updateOrToggleVisibility(project.id, null, true);

    if (
      project.dataLayers[0] &&
      project.dataLayers[0].type === DataLayerType.Story
    ) {
      flyToMarkers(
        project.dataLayers[0].layers.flatMap(
          (markerLayer) => markerLayer.markers
        ) as [[number, number], ...[number, number][]]
      );
    } else if (
      project.dataLayers[0] &&
      project.dataLayers[0].type === DataLayerType.PlainProject
    ) {
      flyToMarkers(project.dataLayers[0].markers);
    }
    // todo zoom to all layers??
    return true;
  };
  const storyLeave = useCallback(() => {
    setView((viewState) => {
      if (viewState !== null && viewState.type === ViewType.Story) {
        return {
          type: ViewType.Regular,
          project: viewState.project,
          contentStyle: ContentDisplayStyle.Short,
        };
      }
      return viewState;
    });
  }, [setView]);
  const storyNavigate = useCallback(
    (increase: boolean): boolean => {
      setView((viewState) => {
        if (!viewState || viewState.type !== ViewType.Story) return viewState;
        const layer = viewState.layer;
        const newMarkerIndex =
          (viewState.markerIndex + (increase ? 1 : -1) + layer.layers.length) %
          layer.layers.length;
        flyToMarkers(layer.layers[newMarkerIndex].markers);
        return {
          ...viewState,
          markerIndex: newMarkerIndex,
        };
      });
      return true;
    },
    [setView]
  );
  const storyNavigateJump = useCallback(
    (project: Project, layer: DataLayerStory, markerIndex?: number) => {
      setView((viewState) => {
        markerIndex = markerIndex || 0;
        if (layer.type !== DataLayerType.Story)
          throw `Cannot open layer '${layer.id}' as story`;
        if (markerIndex < 0 || markerIndex > layer.layers.length - 1)
          throw `Navigating to story index ${markerIndex} not possible for layer ${layer.id}`;
        flyToMarkers(layer.layers[markerIndex].markers);
        return {
          type: ViewType.Story,
          project: project,
          layer: layer,
          markerIndex: markerIndex,
        };
      });
    },
    [setView, flyToMarkers]
  );
  const setContentDisplayStyle = useCallback(
    (contentStyle: ContentDisplayStyle) => {
      setView((viewState) => {
        if (!viewState || viewState.type !== ViewType.Regular) return viewState;
        if (contentStyle !== ContentDisplayStyle.Short)
          resetShortContentTimeout();
        return {
          ...viewState,
          contentStyle: contentStyle,
        };
      });
    },
    [setView]
  );

  const projectById = useCallback(
    (projectId: string): Project | undefined => {
      return projects === null
        ? undefined
        : projects.find((project) => project.id === projectId);
    },
    [projects]
  );

  const activeProjects = useMemo(() => {
    return projects ? activeProjectIds.map((id) => projectById(id)!) : null;
  }, [activeProjectIds, projects]);

  const inactiveProjects = useMemo(() => {
    return projects
      ? projects.filter((project) => !activeProjectIds.includes(project.id))
      : null;
  }, [activeProjectIds, projects]);

  const projectAndLayerById = useCallback(
    (layerId: string): { project: Project; layer: DataLayer } | undefined => {
      if (projects === null) return;
      for (const project of projects) {
        for (const layer of project.dataLayers) {
          if (layer.type !== DataLayerType.PlainProject && layer.id === layerId)
            return { project: project, layer: layer };
        }
      }
    },
    [projects]
  );

  const [hideSidebar, setHideSidebar] = useState(false);

  const resetView: ResetViewCallback = useCallback(
    (mainProjectId: string, furtherPrimaryProjectIds: string[]) => {
      // todo later remove filter, when project relation keys are stable
      const primaryIds = [mainProjectId, ...furtherPrimaryProjectIds].filter(
        (pId) => !!projectById(pId)
      );
      const project = projectById(mainProjectId);
      if (!project)
        throw Error('Project not found for mainProjectId in resetView');
      resetParameterValues();
      setVisibility({});
      setActiveProjectIds(primaryIds);
      // @todo check if this depends on the other ones being run, not sure the dependency works out
      updateOrToggleVisibility(mainProjectId, null, true);
      setPlotLayerAndParams(null);
    },
    [projectById, setVisibility, setActiveProjectIds]
  );

  // @ts-ignore todo fix later
  const showGraph = useCallback(() => {
    if (!projects || !graph)
      throw Error('Cannot show graph before initializing projects');
    showModal(
      <NewGraph
        graph={graph}
        projects={projects}
        resetViewCallback={resetView}
        cancel={closeModal}
      />,
      { closeOnBackgroundClick: true, color: 'light' }
    );
  }, [showModal, projects, graph, closeModal, resetView]);

  const addProjectToPrimaries = (projectId: string) => {
    setActiveProjectIds([projectId, ...activeProjectIds]);
    updateOrToggleVisibility(projectId, null, true);
  };
  const removeProjectFromPrimaries = (projectId: string) => {
    updateOrToggleVisibility(projectId, null, false);
    setActiveProjectIds(
      activeProjectIds.filter((existing) => existing !== projectId)
    );
  };

  const toggleIconFilter = (iconType: DataLayerIcon) => {
    setIconFilters((prev) => {
      const newFilters = new Set(prev);
      if (newFilters.has(iconType)) {
        newFilters.delete(iconType);
      } else {
        newFilters.add(iconType);
      }
      return newFilters;
    });
  };

  const resetIconFilters = () => {
    setIconFilters(new Set());
  };

  const filteredInactiveProjects = useMemo(() => {
    if (!inactiveProjects) return [];
    if (iconFilters.size === 0) return inactiveProjects;

    return inactiveProjects.filter((project) => {
      // Check if project has any data layers that match the filter
      return project.dataLayers.some((layer) => {
        if (
          layer.type === DataLayerType.Story &&
          iconFilters.has(DataLayerIcon.Story)
        ) {
          return true;
        }
        if (layer.type === DataLayerType.Data && iconFilters.has(layer.icon)) {
          return true;
        }
        return false;
      });
    });
  }, [inactiveProjects, iconFilters]);

  const [parameterValues, setParameterValues] = useState<
    Record<string, DataLayerParameterValues>
  >({});
  const [plotLayerAndParams, setPlotLayerAndParams] =
    useState<LayerAndParams | null>(null);

  const resetParameterValues = useCallback(
    (layerId?: string) => {
      if (layerId) {
        const { [layerId]: _, ...result } = parameterValues;
        setParameterValues(result);
      } else {
        setParameterValues({});
      }
    },
    [setParameterValues]
  );

  const updateOrToggleVisibility = useCallback(
    (projectId: string, layer: DataLayer | null, value?: boolean) => {
      const project = projectById(projectId);
      if (project === undefined)
        throw `No project with id "${projectId}" found`;

      if (value !== false && !project.dataLayers.length)
        // todo decide if projects without data layers wanted
        throw `Project with id "${projectId}" does not have dataLayers and cannot be visualized`;

      setVisibility((currentVisibilityState) => {
        const newVisibilityState: Record<string, boolean> = {};
        if (layer === null) {
          // project visibility change
          const newProjectVisibility =
            value ?? !currentVisibilityState[projectId];
          const layers = project.dataLayers;
          let firstDataLayerActivated: DataLayerData | null = null;
          newVisibilityState[project.id] = newProjectVisibility;
          if (newProjectVisibility) {
            // turn on default layers
            let pickedLayerForSidebar: false | DataLayer = false;
            for (const dataLayer of layers) {
              if (dataLayer.type !== DataLayerType.PlainProject) {
                newVisibilityState[getVisibilityKey(project.id, dataLayer.id)] =
                  dataLayer.defaultVisibility;
              }
              if (
                !firstDataLayerActivated &&
                dataLayer.type === DataLayerType.Data &&
                dataLayer.defaultVisibility
              )
                firstDataLayerActivated = dataLayer;
            }
            if (firstDataLayerActivated) {
              flyToDataLayer(firstDataLayerActivated);
              if (firstDataLayerActivated.plots?.length) {
                updateParameterValues(firstDataLayerActivated.id, {});
              }
            }
            if (!currentVisibilityState[projectId])
              viewSetProject(project.id, false, true);
          } else {
            layers.forEach((layer) => {
              if (layer.type === DataLayerType.PlainProject) return;
              newVisibilityState[getVisibilityKey(project.id, layer.id)] =
                false;
              resetParameterValues(layer.id);
            });
            if (view && view.project && view.project.id === projectId)
              viewReset();
          }
        } else {
          // layer visibility change
          const layerVisibilityKey = getVisibilityKey(project.id, layer.id);
          const newLayerVisibility =
            value ?? !currentVisibilityState[layerVisibilityKey];
          newVisibilityState[layerVisibilityKey] = newLayerVisibility;
          if (newLayerVisibility) {
            newVisibilityState[project.id] = true;
            if (!currentVisibilityState[layerVisibilityKey]) {
              if (layer.type === DataLayerType.Data) {
                flyToDataLayer(layer);
                if (layer.plots?.length) {
                  updateParameterValues(layer.id, {});
                }
              }
            }
          }
        }
        return {
          ...currentVisibilityState,
          ...newVisibilityState,
        };
      });
    },
    [
      view,
      projectById,
      setVisibility,
      viewSetProject,
      resetParameterValues,
      flyToDataLayer,
    ]
  );

  const isVisible = useCallback(
    (projectId: string, layerId?: string): boolean => {
      const key = getVisibilityKey(projectId, layerId);
      return visibility[key];
    },
    [visibility]
  );

  const dataLayersForLegend = useMemo((): DataLayerData[] => {
    return (
      activeProjects?.flatMap((project) =>
        project.dataLayers.filter(
          (layer): layer is DataLayerData =>
            isVisible(project.id, layer.id) &&
            layer.type === DataLayerType.Data &&
            (needsControls(layer) ||
              (layer.type === DataLayerType.Data &&
                layer.dataSources.some(
                  (source) => source.coloring.type !== ColoringType.None
                )))
        )
      ) || []
    );
  }, [activeProjects, isVisible]);

  useEffect(() => {
    const aborter = new AbortController();
    getProjects(aborter.signal)
      .then((response) => {
        const { projects } = response;
        setProjects(projects);
        // todo comment/remove
        // setActiveProjectIds(['wofost']);
        // const project = projects.find((p) => p.id === 'wofost');
        // if (!project) throw 'no project found with iddddd "wofost"';
        // setVisibility({
        //   ['wofost']: true,
        //   [`wofost+wofost-model`]: true,
        // });
        // viewSetProject(project.id);
        // flyToMarkers(
        //   layer.layers.flatMap((markerLayer) => markerLayer.markers) as [
        //     [number, number],
        //     ...[number, number][],
        //   ]
        // );
      })
      .catch((e) => {
        if (e === 'component destructor') return; // fail gracefully on signal abort
        console.error(e);
        alert(
          t(
            'Es gab ein Problem beim Laden der Projektdaten, bitte versuchen Sie es erneut, indem Sie neu laden.',
            "There was a problem when loading the projects' data. Please try again by refreshing your browser."
          )
        );
      });
    getGraph(aborter.signal)
      .then((graphResponse) => {
        setGraph(graphResponse);
      })
      .catch((e) => {
        if (e === 'component destructor') return; // fail gracefully on signal abort
        console.error(e);
        alert(
          t(
            'Es gab ein Problem beim Laden des Themengraphen, bitte versuchen Sie es erneut, indem Sie neu laden.',
            'There was a problem when loading the topic graph. Please try again by refreshing your browser.'
          )
        );
      });

    return () => {
      aborter.abort('component destructor');
    };
  }, []);

  useEffect(() => {
    if (projects && graph) showGraph();
  }, [projects, graph]);

  const getParameterValues = useCallback(
    (layerId: string): DataLayerParameterValues => {
      const existingValues = parameterValues[layerId];
      if (existingValues) return existingValues;

      const layerAndProject = projectAndLayerById(layerId);

      if (!layerAndProject)
        throw `Layer with id "${layerId}" not found when reading defaultSettings`;

      const layer = layerAndProject.layer;
      switch (layer.type) {
        case DataLayerType.Story:
          // todo does a default value make sense on get?
          return { index: 0 };
        case DataLayerType.Data:
          const initValues: DataLayerParameterValues = {};
          for (const paramKey in layer.parameters) {
            if (layer.parameters[paramKey].type !== ParameterType.Coupled) {
              initValues[paramKey] = layer.parameters[paramKey].initValue;
            } else {
              const coupledLayerId = layer.parameters[paramKey].dataLayerId;
              const coupledParameter = layer.parameters[paramKey].parameterName;
              const coupledLayerAndProject =
                projectAndLayerById(coupledLayerId);
              if (!coupledLayerAndProject)
                throw `Layer with id "${layerId}" not found when reading defaultSettings`;
              if (coupledLayerAndProject.layer.type === DataLayerType.Story)
                throw `Layer with id "${layerId}" is refering to layerId "${coupledLayerId}" which is a story layer, coupling to stories is not valid`;
              if (
                coupledLayerAndProject.layer.parameters[coupledParameter]
                  .type === ParameterType.Coupled
              )
                throw `Layer with id "${layerId}" is refering to layerId "${coupledLayerId}" parameter "${coupledParameter}" in a chained coupling`;
              initValues[paramKey] =
                coupledLayerAndProject.layer.parameters[
                  coupledParameter
                ].initValue;
            }
          }
          return initValues;
      }
    },
    [parameterValues, projectAndLayerById]
  );
  // this does not reset the layer settings, but instead merges the handed settings into the existing
  // @ts-ignore todo fix later
  const updateParameterValues = useCallback(
    (layerId: string, parameterValues: DataLayerParameterValues) => {
      const projectAndLayer = projectAndLayerById(layerId);
      if (!projectAndLayer)
        throw `No project and layer found for layerId '${layerId}'`;
      setParameterValues((oldParameterValues) => {
        const newLayerParams = {
          ...getParameterValues(layerId),
          ...oldParameterValues[layerId],
          ...parameterValues,
        };
        setPlotLayerAndParams({
          layer: projectAndLayer.layer as DataLayerData,
          params: newLayerParams,
        });
        return {
          ...oldParameterValues,
          [layerId]: newLayerParams,
        };
      });
    },
    [getParameterValues, projectAndLayerById]
  );

  const coordinatePickerLocations = useMemo(() => {
    if (!projects || !(view && view.type !== ViewType.Story)) return [];

    return projects
      .flatMap((proj) =>
        proj.dataLayers.filter(
          (layer): layer is DataLayerData =>
            layer.type === DataLayerType.Data && isVisible(proj.id, layer.id)
        )
      )
      .flatMap(
        (layer) =>
          Object.keys(layer.parameters)
            .filter((paramKey) => {
              return (
                layer.parameters[paramKey].type === ParameterType.Coordinate
              );
            })
            .map((paramKey) => {
              return getParameterValues(layer.id)[paramKey] as [number, number];
            }) as [number, number][]
      );
  }, [
    view,
    projects,
    visibility,
    parameterValues,
    getParameterValues,
    isVisible,
  ]);

  const layerRenderer = (
    project: Project,
    layer: DataLayer | DataLayerPlain,
    parameterValues: DataLayerParameterValues
  ): ReactNode => {
    switch (layer.type) {
      case DataLayerType.Story:
        const layerId = getVisibilityKey(project.id, layer.id);
        return (
          <Fragment key={`${project.id}+${layer.id}`}>
            {layer.layers.map((markerLayer, markerLayerIndex, markerLayers) => (
              <Fragment key={`${layer.id}-${markerLayerIndex}`}>
                {markerLayer.markers.map((marker, multiMarkerIndex) => (
                  <Fragment
                    key={`${layerId}-${markerLayerIndex}-${multiMarkerIndex}`}
                  >
                    <Marker
                      icon={
                        view && view.type === ViewType.Story
                          ? view.layer.id === layer.id
                            ? view.markerIndex === markerLayerIndex
                              ? getColoredMarkerIcon(
                                  subcategoryColors[project.subcategoryId]
                                )
                              : MarkerIcon
                            : MarkerIconInactive
                          : MarkerIcon
                      }
                      zIndexOffset={
                        view && view.type === ViewType.Story
                          ? view.layer.id === layer.id
                            ? view.markerIndex === markerLayerIndex
                              ? 9999999
                              : 9999
                            : 999
                          : 0
                      }
                      position={marker}
                      eventHandlers={{
                        click: (_e) => {
                          // todo still blocking coordinate picker
                          if (coordinatePickerCallback) return _e;
                          storyNavigateJump(project, layer, markerLayerIndex);
                        },
                      }}
                    />
                    {view &&
                      view.type === ViewType.Story &&
                      view.layer.id === layer.id &&
                      markerLayerIndex > 0 &&
                      markerLayers[markerLayerIndex - 1].markers.map(
                        (fromMarker, originIndex) => {
                          const toMarker = marker;
                          return (
                            <Polyline
                              key={`${layerId}+${markerLayerIndex}+${multiMarkerIndex}+${originIndex}`}
                              positions={[fromMarker, toMarker]}
                              smoothFactor={0.5}
                              color={'#CCCCCC'}
                              dashArray={[3, 0, 3]}
                            />
                          );
                        }
                      )}
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </Fragment>
        );
      case DataLayerType.PlainProject:
        return (
          <Fragment key={`${project.id}+plainproject`}>
            {layer.markers.map((marker, markerIndex) => (
              <Marker
                key={`${project.id}+plainproject+${markerIndex}`}
                icon={
                  !view
                    ? MarkerIcon
                    : view.project.dataLayers[0].type ===
                          DataLayerType.PlainProject &&
                        view.project.id === project.id
                      ? getColoredMarkerIcon(
                          subcategoryColors[project.subcategoryId]
                        )
                      : view.type === ViewType.Story
                        ? MarkerIconInactive
                        : MarkerIcon
                }
                position={marker}
                eventHandlers={{
                  click: (_e) => {
                    // todo still blocking coordinate picker
                    if (coordinatePickerCallback) return _e;
                    viewSetProject(project.id);
                  },
                }}
              />
            ))}
          </Fragment>
        );
      case DataLayerType.Data:
        const layerParamValues = getParameterValues(layer.id);

        return layer.dataSources.map((dataSource, dataSourceIndex) => {
          switch (dataSource.dataSource.type) {
            case SourceType.TilesTMS:
              return renderTiles(
                layer,
                dataSource.dataSource,
                `${project.id}+${layer.id}+${dataSourceIndex}`,
                dataSource.coloring,
                layerParamValues
              );
            case SourceType.GeoRaster:
              return renderRaster(
                layer,
                dataSource.dataSource,
                `${project.id}+${layer.id}+${dataSourceIndex}`,
                dataSource.coloring,
                layerParamValues
              );
            case SourceType.GeoJson:
              return renderGeoJson(
                layer,
                dataSource.dataSource,
                `${project.id}+${layer.id}+${dataSourceIndex}`,
                dataSource.coloring,
                layerParamValues
              );
          }
        });
    }
  };

  const renderTiles = (
    layer: DataLayerData,
    dataSource: SourceTilesTMS,
    key: string,
    coloring: Coloring,
    parameterValues: DataLayerParameterValues
  ): ReactNode => {
    const urlWithParameters = substituteParameters(
      dataSource.url,
      parameterValues,
      layer.parameters
    );

    return (
      <ColoredTileLayer
        key={key + urlWithParameters}
        url={urlWithParameters}
        minNativeZoom={dataSource.minZoom}
        maxNativeZoom={dataSource.maxZoom}
        tms={true}
        opacity={0.7}
        // nodataValue={0}
        coloring={coloring}
        bounds={new LatLngBounds(dataSource.bounds[0], dataSource.bounds[1])}
      />
    );
  };
  const renderRaster = (
    layer: DataLayerData,
    dataSource: SourceGeoRaster,
    key: string,
    coloring: Coloring,
    parameterValues: DataLayerParameterValues
  ): ReactNode => {
    const urlWithParameters = substituteParameters(
      dataSource.url,
      parameterValues,
      layer.parameters
    );
    return (
      <GeoRasterLayer
        key={key + urlWithParameters}
        url={urlWithParameters}
        coloring={coloring}
      />
    );
  };
  const renderGeoJson = (
    layer: DataLayerData,
    dataSource: SourceGeoJson,
    key: string,
    coloring: Coloring,
    parameterValues: DataLayerParameterValues
  ): ReactNode => {
    const urlWithParameters = substituteParameters(
      dataSource.url,
      parameterValues,
      layer.parameters
    );
    return (
      <GeoJsonLayer
        key={key + urlWithParameters}
        url={urlWithParameters}
        coloring={coloring}
        property={
          dataSource.parameterForPropertySelectorColoring
            ? parameterValues[
                dataSource.parameterForPropertySelectorColoring
              ].toString()
            : dataSource.propertyForSelectorColoring ?? undefined
        }
      />
    );
  };

  return (
    (projects && graph && activeProjects && inactiveProjects && visibility && (
      <MapContext.Provider
        value={{
          projects: projects,
          graph: graph,
          subcategoryColors: subcategoryColors,
          colorForSubcategoryId: colorForSubcategoryId,
          gradientClassesForSubcategoryId: gradientClassesForSubcategoryId,
          projectById: projectById,
          activeProjects: activeProjects,
          inactiveProjects: inactiveProjects,
          projectAndLayerById: projectAndLayerById,
          view: view,
          viewReset: viewReset,
          viewSetProject: viewSetProject,
          shortContentRerender: shortContentRerender,
          shortContentTimeout: shortContentTimeout.current,
          resetShortContentTimeout: resetShortContentTimeout,
          storyLeave: storyLeave,
          storyNavigate: storyNavigate,
          storyNavigateJump: storyNavigateJump,
          setContentDisplayStyle: setContentDisplayStyle,
          hideSidebar: hideSidebar,
          resetView: resetView,
          showGraph: showGraph,
          addProjectToPrimaries: addProjectToPrimaries,
          removeProjectFromPrimaries: removeProjectFromPrimaries,
          updateOrToggleVisibility: updateOrToggleVisibility,
          isVisible: isVisible,
          dataLayersForLegend: dataLayersForLegend,
          getParameterValues: getParameterValues,
          updateParameterValues: updateParameterValues,
          coordinatePickerLocations: coordinatePickerLocations,
        }}
      >
        <CoordinatePickerContext.Provider
          value={{
            requestCoordinate: (callback: CoordinatePickerCallback) => {
              setCoordinatePickerCallback(() => callback);
            },
            currentRequest: coordinatePickerCallback,
            resetRequest: () => {
              setCoordinatePickerCallback(null);
            },
          }}
        >
          <MapContainer
            center={initialMapCenter}
            zoom={initialMapZoom}
            className={classNames(
              'absolute',
              'inset-0',
              'z-20',
              coordinatePickerCallback !== null
                ? 'cursor-crosshair!'
                : undefined
            )}
            zoomControl={false}
          >
            <CoordinatePickerHandle />
            <ZoomControl
              maxZoom={flyToMaxZoom}
              bounds={flyToBounds}
              zoomLock={zoomLock}
              leftSidebarWidth={
                view !== null && view.type === ViewType.Story ? 660 : 0
              }
              rightSidebarWidth={!hideSidebar ? 330 : 0}
            />
            {coordinatePickerLocations.map((location) => (
              <Marker
                key={`${location[0]}+${location[1]}`}
                icon={MarkerIconOrange}
                zIndexOffset={99999999}
                position={location}
              />
            ))}
            {/* https://leaflet-extras.github.io/leaflet-providers/preview/ */}
            {/*<TileLayer*/}
            {/*  attribution='Imagery &copy; <a href="https://www.mapbox.com/">Mapbox</a>'*/}
            {/*  url={`https://api.mapbox.com/styles/v1/${MAPBOX_USERNAME}/${MAPBOX_STYLE_ID}/tiles/256/{z}/{x}/{y}@2x?access_token=${MAPBOX_ACCESS_TOKEN}`}*/}
            {/*/>*/}
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attribution">CartoDB</a>'
              url={`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png`}
            />
            {/*<TileLayer*/}
            {/*  attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'*/}
            {/*  url={`https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png`}*/}
            {/*/>*/}
            {activeProjects?.flatMap((project) =>
              project.dataLayers
                .filter(
                  (layer) =>
                    layer.type === DataLayerType.PlainProject ||
                    isVisible(project.id, layer.id)
                )
                .map((layer) => {
                  return layerRenderer(
                    project,
                    layer,
                    layer.type !== DataLayerType.PlainProject
                      ? getParameterValues(layer.id)
                      : {}
                  );
                })
            )}
          </MapContainer>
          {hideSidebar && (
            <NeumorphBox
              className={classNames(
                'absolute',
                'top-[30px]',
                'right-[30px]',
                'w-[40px]',
                'h-[40px]',
                'flex',
                'flex-col',
                'justify-center',
                'p-[6px]!',
                'z-999',
                hideSidebar ? undefined : 'opacity-0'
              )}
              type={NeumorphType.Hoverable}
              onClick={() => {
                setHideSidebar(false);
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </NeumorphBox>
          )}
          {/* RIGHT SIDEBAR START */}
          <div
            className={classNames(
              'absolute',
              'top-[30px]',
              'bottom-[30px]',
              'right-[30px]',
              'w-[270px]',
              'shadow-lg',
              'rounded-[12px]',
              'z-1000',
              'text-sm',
              'bg-gray-100',
              'cursor-default',
              hideSidebar ? 'translate-x-[350px]' : undefined,
              'transition-translate',
              'duration-500'
            )}
          >
            <Panel
              className={classNames(
                'relative',
                'h-[calc(100%-170px)]',
                'overflow-y-auto',
                '-ml-[40px]',
                'pl-[40px]',
                addingPrimaryProject ? 'opacity-0' : 'opacity-100',
                'transition-opacity'
              )}
            >
              <div
                className={classNames(
                  'flex',
                  'flex-row-reverse',
                  'justify-between',
                  'mt-[12px]'
                )}
              >
                <NeumorphBox
                  className={classNames(
                    'w-[30px]',
                    'h-[30px]',
                    'flex',
                    'flex-col',
                    'justify-center',
                    'p-[6px]!'
                  )}
                  type={NeumorphType.Hoverable}
                  onClick={() => {
                    setHideSidebar(true);
                  }}
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </NeumorphBox>
                <div className={cn('flex', 'gap-x-[4px]')}>
                  <NeumorphBox
                    className={classNames(
                      'w-[30px]',
                      'h-[30px]',
                      'flex',
                      'flex-col',
                      'justify-center',
                      'p-[6px]!',
                      'mx-0'
                    )}
                    type={NeumorphType.Hoverable}
                    onClick={() => {
                      setZoomLock(!zoomLock);
                    }}
                  >
                    <FontAwesomeIcon icon={zoomLock ? faLock : faLockOpen} />
                  </NeumorphBox>
                  <NeumorphBox
                    className={classNames(
                      'w-[30px]',
                      'h-[30px]',
                      'flex',
                      'flex-col',
                      'justify-center',
                      'p-[6px]!',
                      'mx-0'
                    )}
                    type={NeumorphType.Hoverable}
                    onClick={() => {
                      toggleLang();
                    }}
                  >
                    {t('EN', 'DE')}
                  </NeumorphBox>
                </div>
                {Boolean(inactiveProjects.length) && (
                  <NeumorphBox
                    className={classNames(
                      'w-[30px]',
                      'h-[30px]',
                      'flex',
                      'flex-col',
                      'justify-center',
                      'p-[6px]!'
                    )}
                    type={NeumorphType.Hoverable}
                    onClick={() => {
                      setAddingPrimaryProject(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </NeumorphBox>
                )}
              </div>

              {activeProjects.map((project, _) => (
                <div
                  className={classNames(
                    'group/project',
                    'relative',
                    'my-[12px]',
                    'mx-[12px]',
                    'pl-[18px]'
                  )}
                  key={project.id}
                >
                  <div
                    className={classNames(
                      'absolute',
                      'top-0',
                      'bottom-0',
                      'left-0',
                      'w-[12px]',
                      'rounded-[6px]',
                      view && view.project.id === project.id
                        ? gradientClassesForSubcategoryId(project.subcategoryId)
                        : ['bg-linear-to-br', 'from-gray-200', 'to-gray-300']
                    )}
                  ></div>

                  <div
                    key={project.id}
                    className={classNames(
                      'relative',
                      'rounded-[6px]',
                      'bg-gray-200'
                    )}
                  >
                    <div
                      className={classNames(
                        'relative',
                        'py-[12px]',
                        'pl-[12px]',
                        'pr-[32px]',
                        'transition-[padding]'
                      )}
                    >
                      <div
                        className={classNames(
                          'transition-[font-size]',
                          'group-hover/project:translate-x-[24px]',
                          'transition-transform'
                        )}
                      >
                        {t(project.shortName, project.shortNameDe)}
                      </div>

                      <span
                        onClick={() => {
                          removeProjectFromPrimaries(project.id);
                        }}
                        className={classNames(
                          'absolute',
                          'left-[12px]',
                          'top-1/2',
                          '-translate-y-1/2',
                          'text-gray-700',
                          view && view.project.id === project.id
                            ? 'hover:text-neutral-300'
                            : hoverTextColorClassesForColor(
                                colorForSubcategoryId(project.subcategoryId)
                              ),
                          'cursor-pointer',
                          'opacity-0',
                          'group-hover/project:opacity-100',
                          'transition-opacity'
                        )}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </span>

                      <span
                        onClick={() => {
                          viewSetProject(project.id);
                        }}
                        className={classNames(
                          'absolute',
                          'right-0',
                          'px-[12px]',
                          'top-0',
                          'bottom-0',
                          view && view.project.id === project.id
                            ? 'hover:text-neutral-300'
                            : hoverTextColorClassesForColor(
                                colorForSubcategoryId(project.subcategoryId)
                              ),
                          'group-hover/project:bg-linear-to-r',
                          'from-transparent',
                          'via-20%',
                          'via-gray-200',
                          'to-gray-200',
                          'rounded-r-[6px]',

                          'cursor-pointer',
                          'flex',
                          'flex-col',
                          'justify-center',
                          view && view.project.id === project.id
                            ? 'text-gray-500'
                            : 'text-gray-300'
                        )}
                      >
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </span>
                    </div>

                    {project.dataLayers.map((layer) => {
                      if (layer.type === DataLayerType.PlainProject) return;
                      const key = getVisibilityKey(project.id, layer.id);
                      return (
                        <div
                          className={classNames(
                            'group/layer',
                            'relative',
                            'rounded-[6px]',
                            'my-[6px]',
                            'pl-[18px]',
                            '-ml-[18px]',
                            !isVisible(project.id, layer.id)
                              ? 'cursor-pointer'
                              : undefined
                          )}
                          key={key}
                          onClick={() => {
                            if (!isVisible(project.id, layer.id)) {
                              updateOrToggleVisibility(project.id, layer);
                            }
                          }}
                        >
                          <div
                            className={classNames(
                              'bg-gray-200',
                              'p-[12px]',
                              'rounded-[6px]',
                              'bg-linear-to-r',
                              'from-gray-300',
                              'to-10%',
                              'to-transparent'
                            )}
                          >
                            {isVisible(project.id, layer.id) && (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateOrToggleVisibility(project.id, layer);
                                }}
                                className={classNames(
                                  'cursor-pointer',
                                  'absolute',
                                  'top-0',
                                  'bottom-0',
                                  '-left-[28px]',
                                  'w-[40px]',
                                  'rounded-r-[6px]',
                                  'rounded-l-[100%]',
                                  'text-gray-100',
                                  gradientClassesForSubcategoryId(
                                    project.subcategoryId
                                  ),
                                  'transition-all',
                                  'duration-300',
                                  'hover:bg-linear-to-br',
                                  'hover:from-gray-200',
                                  'hover:to-gray-300',
                                  'hover:text-gray-700'
                                )}
                              >
                                <span
                                  className={classNames(
                                    'absolute',
                                    'top-1/2',
                                    'right-[12px]',
                                    'translate-y-[-50%]'
                                  )}
                                >
                                  <FontAwesomeIcon
                                    className={classNames(
                                      'block',
                                      'group-hover/layer:hidden!',
                                      '-mr-[3px]'
                                    )}
                                    icon={iconForDataLayer(layer)}
                                  />
                                  <FontAwesomeIcon
                                    className={classNames(
                                      'hidden!',
                                      'group-hover/layer:block!',
                                      '-mr-[3px]'
                                    )}
                                    icon={faEyeSlash}
                                  />
                                </span>
                              </div>
                            )}
                            {!isVisible(project.id, layer.id) && (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateOrToggleVisibility(project.id, layer);
                                }}
                                className={classNames(
                                  'cursor-pointer',
                                  'absolute',
                                  'top-0',
                                  'bottom-0',
                                  'left-0',
                                  'w-[12px]',
                                  'rounded-[6px]',
                                  'text-gray-100',
                                  gradientClassesForSubcategoryId(
                                    project.subcategoryId
                                  ),
                                  'transition-all',
                                  'duration-300',
                                  'group-hover/layer:rounded-r-[6px]',
                                  'group-hover/layer:rounded-l-[100%]',
                                  'group-hover/layer:w-[40px]', // width
                                  'group-hover/layer:-left-[28px]', // width + 6
                                  'hover:bg-linear-to-br',
                                  'hover:from-gray-200',
                                  'hover:to-gray-300',
                                  'hover:text-gray-700'
                                )}
                              >
                                <span
                                  className={classNames(
                                    'absolute',
                                    'hidden',
                                    'group-hover/layer:block',
                                    'top-1/2',
                                    'right-[12px]',
                                    'translate-y-[-50%]'
                                  )}
                                >
                                  <FontAwesomeIcon
                                    className={classNames('-mr-[3px]')}
                                    icon={faEye}
                                  />
                                </span>
                              </div>
                            )}

                            <span>{t(layer.shortName, layer.shortNameDe)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </Panel>

            <GraphLink
              className={classNames(
                'absolute',
                'left-[30px]',
                'right-[30px]',
                'bottom-[12px]'
              )}
              onClick={() => {
                if (projects) {
                  showGraph();
                }
              }}
            />

            {/* INACTIVE PROJECTS START */}
            <Panel
              className={classNames(
                'absolute',
                'opacity-0',
                addingPrimaryProject
                  ? ['inset-y-[12px]', 'inset-x-0', 'opacity-100', 'scale-100']
                  : ['w-0', 'h-0', 'scale-50'],
                'bg-gray-100',
                'overflow-y-auto',
                'duration-500',
                'transition-opacity',
                'transition-translate'
              )}
            >
              {/* Filter Controls Header */}
              <div
                className={classNames(
                  'flex',
                  'justify-between',
                  'items-center',
                  'mb-3'
                )}
              >
                <NeumorphBox
                  className={classNames(
                    'w-[30px]',
                    'h-[30px]',
                    'flex',
                    'flex-col',
                    'justify-center',
                    'p-[6px]!'
                  )}
                  type={NeumorphType.Hoverable}
                  onClick={() => {
                    setAddingPrimaryProject(false);
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </NeumorphBox>

                <div className={classNames('flex', 'gap-1')}>
                  {/* Story Filter Button */}
                  <NeumorphBox
                    className={classNames(
                      'w-[30px]',
                      'h-[30px]',
                      'flex',
                      'flex-col',
                      'justify-center',
                      'p-[6px]!',
                      iconFilters.has(DataLayerIcon.Story)
                        ? undefined
                        : iconFilters.size > 0
                          ? 'text-gray-300!'
                          : undefined
                    )}
                    type={NeumorphType.Hoverable}
                    onClick={() => toggleIconFilter(DataLayerIcon.Story)}
                  >
                    <FontAwesomeIcon icon={faScroll} />
                  </NeumorphBox>

                  {/* Static Filter Button */}
                  <NeumorphBox
                    className={classNames(
                      'w-[30px]',
                      'h-[30px]',
                      'flex',
                      'flex-col',
                      'justify-center',
                      'p-[6px]!',
                      iconFilters.has(DataLayerIcon.Static)
                        ? undefined
                        : iconFilters.size > 0
                          ? 'text-gray-300!'
                          : undefined
                    )}
                    type={NeumorphType.Hoverable}
                    onClick={() => toggleIconFilter(DataLayerIcon.Static)}
                  >
                    <FontAwesomeIcon icon={faLayerGroup} />
                  </NeumorphBox>

                  {/* Series Filter Button */}
                  <NeumorphBox
                    className={classNames(
                      'w-[30px]',
                      'h-[30px]',
                      'flex',
                      'flex-col',
                      'justify-center',
                      'p-[6px]!',
                      iconFilters.has(DataLayerIcon.Series)
                        ? undefined
                        : iconFilters.size > 0
                          ? 'text-gray-300!'
                          : undefined
                    )}
                    type={NeumorphType.Hoverable}
                    onClick={() => toggleIconFilter(DataLayerIcon.Series)}
                  >
                    <FontAwesomeIcon icon={faClockRotateLeft} />
                  </NeumorphBox>

                  {/* Model Filter Button */}
                  <NeumorphBox
                    className={classNames(
                      'w-[30px]',
                      'h-[30px]',
                      'flex',
                      'flex-col',
                      'justify-center',
                      'p-[6px]!',
                      iconFilters.has(DataLayerIcon.Model)
                        ? undefined
                        : iconFilters.size > 0
                          ? 'text-gray-300!'
                          : undefined
                    )}
                    type={NeumorphType.Hoverable}
                    onClick={() => toggleIconFilter(DataLayerIcon.Model)}
                  >
                    <FontAwesomeIcon icon={faNetworkWired} />
                  </NeumorphBox>
                </div>
              </div>

              {filteredInactiveProjects.map((project) => (
                <NeumorphBox
                  key={project.id}
                  type={NeumorphType.HoverableInset}
                  className={classNames(
                    'relative',
                    'block',
                    'group/addproject',
                    'overflow-hidden'
                  )}
                  onClick={() => {
                    addProjectToPrimaries(project.id);
                    setAddingPrimaryProject(false);
                  }}
                >
                  <span
                    className={classNames(
                      'relative',
                      'left-0',
                      'group-hover/addproject:left-[24px]',
                      'transition-all',
                      'duration-700'
                    )}
                  >
                    <span
                      className={classNames(
                        'absolute',
                        'opacity-0',
                        'group-hover/addproject:opacity-100',
                        'left-0',
                        'group-hover/addproject:-left-[24px]',
                        'top-1/2',
                        '-translate-y-1/2',
                        'transition-all',
                        'duration-700'
                      )}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </span>
                    {project.dataLayers
                      .filter(
                        (layer) => layer.type !== DataLayerType.PlainProject
                      )
                      .map((layer, index) => (
                        <FontAwesomeIcon
                          key={index}
                          icon={iconForDataLayer(layer)}
                          className={classNames(
                            'mr-1',
                            index === 0 ? 'mr-2' : ''
                          )}
                        />
                      ))}
                    {t(project.shortName, project.shortNameDe)}
                  </span>
                  <div
                    className={classNames(
                      'absolute',
                      'top-0',
                      'bottom-0',
                      'right-0',
                      'w-[40px]',
                      'hidden',
                      'group-hover/addproject:block',
                      'bg-linear-to-r',
                      'from-transparent',
                      'to-gray-100'
                    )}
                  ></div>
                </NeumorphBox>
              ))}
            </Panel>
            {/* INACTIVE PROJECTS END */}
          </div>

          <PanelLegend />

          <PanelContent plotPanelLarge={plotPanelLarge} />

          <PanelStory />

          <PanelPlots
            layerAndParams={plotLayerAndParams}
            propagateSize={(value?: boolean) => {
              setPlotPanelLarge(() => value);
            }}
          />

          {!disclaimerRead && <Disclaimer hide={onDisclaimerRead} />}
        </CoordinatePickerContext.Provider>
      </MapContext.Provider>
    )) || (
      <div
        className={classNames(
          'h-full',
          'relative',
          'flex',
          'justify-center',
          'align-center'
        )}
      >
        <Loading />
      </div>
    )
  );
}
